<template>
  <v-container fluid>
    <h5 class="grey--text section-label">{{ $t('supportRequests.title') }}</h5>
    <v-row>
      <v-col md="12">
        <h2 class="deep-purple--text lighten-3">{{ $t('supportRequests.subtitle') }}</h2>
      </v-col>
    </v-row>

    <v-btn-toggle v-model="lane" mandatory dense borderless class="mb-4">
      <v-btn v-for="l in LANES" :key="l" :value="l">
        {{ $t(`supportRequests.lanes.${l}`) }} ({{ counts.byLane[l] || 0 }})
      </v-btn>
    </v-btn-toggle>
    <v-btn
      small
      outlined
      class="mb-4 ml-2"
      :color="viewMode === 'review' ? 'deep-purple' : undefined"
      :dark="viewMode === 'review'"
      @click="toggleReviewMode"
    >
      {{ $t('supportRequests.review.tab_label') }}
    </v-btn>

    <div v-if="actionTypeCounts.length" class="d-flex flex-wrap mb-3" style="gap: 8px">
      <v-chip
        v-for="entry in actionTypeCounts"
        :key="entry.type"
        small
        :outlined="filters.actionType !== entry.type"
        :color="filters.actionType === entry.type ? 'deep-purple' : undefined"
        :dark="filters.actionType === entry.type"
        @click="selectActionType(entry.type)"
      >
        {{ $t(`supportRequests.action_types.${entry.type}`) }} — {{ entry.count }}
      </v-chip>
      <v-chip v-if="filters.actionType" small text @click="selectActionType(null)">
        {{ $t('supportRequests.filters.all_action_types') }}
      </v-chip>
    </div>

    <support-request-filters v-model="filters" class="mb-2" @search="onSearch" />

    <div
      v-if="lane === 'READY_TO_APPROVE' && actions.length"
      class="d-flex align-center mb-2"
    >
      <v-checkbox
        :input-value="allSelected"
        :indeterminate="selectedIds.length > 0 && !allSelected"
        hide-details
        class="mt-0 pt-0 mr-4"
        :label="$t('supportRequests.batch.select_all')"
        @change="toggleSelectAll"
      />
      <v-btn
        color="deep-purple"
        dark
        small
        :disabled="!selectedIds.length || batchApproving"
        :loading="batchApproving"
        @click="approveSelectedBatch"
      >
        {{ $t('supportRequests.batch.approve_selected', { count: selectedIds.length }) }}
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="deep-purple" class="mb-4" />

    <v-alert v-else-if="emptyStateKind === 'filtered'" type="info" text>
      {{ $t('supportRequests.counts.empty_for_type', {
        type: $t(`supportRequests.action_types.${filters.actionType}`),
      }) }}
      <div class="mt-2">
        <v-btn text small color="deep-purple" @click="selectActionType(null)">
          {{ $t('supportRequests.filters.all_action_types') }}
        </v-btn>
      </div>
    </v-alert>

    <v-alert v-else-if="emptyStateKind === 'generic'" type="info" text>
      {{ $t('supportRequests.counts.empty') }}
    </v-alert>

    <template v-else>
      <support-request-action-card
        v-for="action in actions"
        :key="action.id"
        :action="action"
        :show-select="viewMode === 'lanes' && lane === 'READY_TO_APPROVE'"
        :selected="selectedIds.includes(action.id)"
        :busy="busyId === action.id"
        @toggle-select="toggleSelect(action.id)"
        @approve="approveAction(action, $event)"
        @reject="rejectAction(action)"
        @pick-candidate="pickCandidate(action, $event)"
        @reply-draft-saved="onReplyDraftSaved(action, $event)"
        @undo="undoAction(action)"
      />
    </template>

    <div v-if="totalPages > 1" class="text-center pt-2">
      <v-pagination
        v-model="page"
        :length="totalPages"
        circle
        :total-visible="7"
        @input="loadActions"
      />
    </div>

    <support-request-unprocessed-list
      v-if="lane === 'MANUAL' && viewMode === 'lanes'"
      :filters="{ schoolId: filters.schoolId, dateFrom: filters.dateFrom, dateTo: filters.dateTo }"
    />
  </v-container>
</template>

<script>
import supportRequestService, { VersionConflictError } from '@/services/supportRequest.service';
import { SupportRequestLane } from '@/constants/supportRequests';
import { actionTypeCountsForLane } from '@/utils/supportRequests';
import SupportRequestFilters from '@/components/supportRequests/SupportRequestFilters.vue';
import SupportRequestActionCard from '@/components/supportRequests/SupportRequestActionCard.vue';
import SupportRequestUnprocessedList from '@/components/supportRequests/SupportRequestUnprocessedList.vue';
import snackbarMixin from '@/mixins/snackbar';

const LANES = [
  SupportRequestLane.READY_TO_APPROVE,
  SupportRequestLane.NEEDS_DECISION,
  SupportRequestLane.MANUAL,
];

export default {
  name: 'SupportRequests',
  components: { SupportRequestFilters, SupportRequestActionCard, SupportRequestUnprocessedList },
  mixins: [snackbarMixin],
  data: () => ({
    LANES,
    lane: SupportRequestLane.READY_TO_APPROVE,
    viewMode: 'lanes',
    filters: {
      actionType: null, schoolId: null, dateFrom: null, dateTo: null,
    },
    page: 1,
    perPage: 25,
    totalPages: 1,
    actions: [],
    counts: {
      byActionType: {}, byLane: {}, byActionTypeAndLane: {},
    },
    loading: false,
    // Distingue "la carga falló" de "no hay resultados", para no mostrarle a
    // Soporte un mensaje de filtro vacío cuando en realidad el request se cayó.
    loadFailed: false,
    selectedIds: [],
    busyId: null,
    batchApproving: false,
    // Token de generación: mismo patrón que Surveys.vue — evita que una
    // respuesta vieja (carril/filtro/página anteriores) pise el estado de la
    // consulta más reciente cuando llega tarde.
    requestId: 0,
  }),
  computed: {
    allSelected() {
      return this.actions.length > 0 && this.selectedIds.length === this.actions.length;
    },
    // Acotado al carril activo (KB-907): usa byActionTypeAndLane cuando el
    // backend lo manda, y cae al byActionType global si no.
    actionTypeCounts() {
      return actionTypeCountsForLane(this.counts, this.lane);
    },
    // 'filtered': hay filtro de tipo activo y no hay resultados para esa
    // combinación carril+tipo (no es que el carril esté vacío). 'generic':
    // el mensaje de siempre — también cuando la carga falló, para no mentir
    // "no hay gestiones de este tipo" si en realidad el request se cayó (ya
    // hay un snackbar de error avisando eso).
    emptyStateKind() {
      if (this.actions.length || this.loading) return null;
      if (!this.loadFailed && this.viewMode === 'lanes' && this.filters.actionType) return 'filtered';
      return 'generic';
    },
  },
  watch: {
    lane() {
      this.viewMode = 'lanes';
      this.page = 1;
      this.selectedIds = [];
      this.loadActions();
    },
  },
  async mounted() {
    await Promise.all([this.loadCounts(), this.loadActions()]);
  },
  methods: {
    async loadCounts() {
      try {
        this.counts = await supportRequestService.counts();
      } catch (_error) {
        // Los contadores son un detalle del encabezado: si fallan, la bandeja
        // sigue siendo usable con la lista de acciones.
        this.counts = { byActionType: {}, byLane: {}, byActionTypeAndLane: {} };
      }
    },
    async loadActions() {
      this.loading = true;
      this.requestId += 1;
      const { requestId } = this;
      try {
        const params = this.viewMode === 'review'
          ? {
            ...this.filters, status: 'EXECUTED_WITH_WARNING', page: this.page, perPage: this.perPage,
          }
          : {
            ...this.filters, lane: this.lane, page: this.page, perPage: this.perPage,
          };
        const result = await supportRequestService.listActions(params);
        if (requestId !== this.requestId) return;
        this.actions = result.data;
        this.totalPages = result.totalPages;
        this.selectedIds = [];
        this.loadFailed = false;
      } catch (_error) {
        if (requestId !== this.requestId) return;
        this.actions = [];
        this.loadFailed = true;
        this.snackbarError(this.$t('supportRequests.errors.load'));
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.loadActions();
    },
    toggleReviewMode() {
      this.viewMode = this.viewMode === 'review' ? 'lanes' : 'review';
      this.page = 1;
      this.loadActions();
    },
    selectActionType(type) {
      this.filters = { ...this.filters, actionType: type };
      this.page = 1;
      this.loadActions();
    },
    toggleSelectAll() {
      this.selectedIds = this.allSelected ? [] : this.actions.map((a) => a.id);
    },
    toggleSelect(id) {
      this.selectedIds = this.selectedIds.includes(id)
        ? this.selectedIds.filter((sid) => sid !== id)
        : [...this.selectedIds, id];
    },
    onReplyDraftSaved(action, replyDraft) {
      // Editar el borrador no cambia carril ni contadores: alcanza con
      // actualizar el item local, sin recargar toda la lista.
      const found = this.actions.find((a) => a.id === action.id);
      if (found) found.supportRequest.replyDraft = replyDraft;
    },
    async refreshAfterMutation() {
      await Promise.all([this.loadActions(), this.loadCounts()]);
    },
    handleMutationError(error, fallbackKey) {
      if (error instanceof VersionConflictError) {
        this.snackbarError(this.$t('supportRequests.errors.conflict'));
        this.refreshAfterMutation();
        return;
      }
      this.snackbarError(this.$t(fallbackKey));
    },
    async approveAction(action, { targetIds }) {
      this.busyId = action.id;
      try {
        await supportRequestService.approve(action.id, action.version, targetIds);
        this.snackbarSuccess(this.$t('supportRequests.success.approved'));
        await this.refreshAfterMutation();
      } catch (error) {
        this.handleMutationError(error, 'supportRequests.errors.approve');
      } finally {
        this.busyId = null;
      }
    },
    async rejectAction(action) {
      this.busyId = action.id;
      try {
        await supportRequestService.reject(action.id, action.version);
        this.snackbarSuccess(this.$t('supportRequests.success.rejected'));
        await this.refreshAfterMutation();
      } catch (error) {
        this.handleMutationError(error, 'supportRequests.errors.reject');
      } finally {
        this.busyId = null;
      }
    },
    async pickCandidate(action, { targetId, bookuserId }) {
      this.busyId = action.id;
      try {
        await supportRequestService.pickCandidate(targetId, action.version, bookuserId);
        this.snackbarSuccess(this.$t('supportRequests.success.picked'));
        await this.refreshAfterMutation();
      } catch (error) {
        this.handleMutationError(error, 'supportRequests.errors.pick');
      } finally {
        this.busyId = null;
      }
    },
    async undoAction(action) {
      this.busyId = action.id;
      try {
        await supportRequestService.undo(action.id, action.version);
        this.snackbarSuccess(this.$t('supportRequests.success.undone'));
        await this.refreshAfterMutation();
      } catch (error) {
        this.handleMutationError(error, 'supportRequests.errors.undo');
      } finally {
        this.busyId = null;
      }
    },
    async approveSelectedBatch() {
      this.batchApproving = true;
      const targets = this.actions.filter((a) => this.selectedIds.includes(a.id));
      const results = await Promise.allSettled(
        targets.map((a) => supportRequestService.approve(a.id, a.version)),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const success = results.length - failed;
      if (failed === 0) {
        this.snackbarSuccess(this.$t('supportRequests.batch.result_success', { count: success }));
      } else {
        this.snackbarWarning(this.$t('supportRequests.batch.result_partial', { success, failed }));
      }
      this.selectedIds = [];
      this.batchApproving = false;
      await this.refreshAfterMutation();
    },
  },
};
</script>

<style scoped>
.section-label {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
